import React from 'react';

// Rich animated background for the hero: floating molecule / DNA / neuron
// nodes, a slow-moving grid, orbital rings, and an ECG waveform strip.
// All pure-SVG + CSS animations — no external libs, no heavy assets.
export default function HeroAnimations() {
  return (
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      {/* light orange radial glows */}
      <div className="absolute -top-32 -left-24 w-[560px] h-[560px] rounded-full bg-[radial-gradient(closest-side,#FFD5B4,transparent_70%)] opacity-70 animate-[blob_18s_ease-in-out_infinite]" />
      <div className="absolute top-10 right-[-120px] w-[520px] h-[520px] rounded-full bg-[radial-gradient(closest-side,#FFE9D6,transparent_70%)] opacity-80 animate-[blob_22s_ease-in-out_infinite_reverse]" />
      <div className="absolute bottom-[-160px] left-1/3 w-[600px] h-[600px] rounded-full bg-[radial-gradient(closest-side,#FFF1E1,transparent_75%)] opacity-90" />

      {/* moving grid */}
      <div
        className="absolute inset-0 opacity-[0.06] animate-[gridDrift_60s_linear_infinite]"
        style={{
          backgroundImage:
            'linear-gradient(#0F1B2D 1px, transparent 1px), linear-gradient(90deg, #0F1B2D 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* Neural network / molecule constellation */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1200 800" preserveAspectRatio="none">
        <defs>
          <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FF7A2E" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#FF7A2E" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="linkGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FF7A2E" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#FF7A2E" stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Constellation links */}
        <g stroke="url(#linkGrad)" strokeWidth="0.7" fill="none">
          <line x1="110" y1="140" x2="260" y2="210" />
          <line x1="260" y1="210" x2="420" y2="120" />
          <line x1="420" y1="120" x2="560" y2="280" />
          <line x1="260" y1="210" x2="340" y2="420" />
          <line x1="560" y1="280" x2="720" y2="180" />
          <line x1="720" y1="180" x2="880" y2="320" />
          <line x1="880" y1="320" x2="1040" y2="220" />
          <line x1="1040" y1="220" x2="1130" y2="120" />
          <line x1="340" y1="420" x2="520" y2="540" />
          <line x1="520" y1="540" x2="720" y2="620" />
          <line x1="720" y1="620" x2="900" y2="540" />
          <line x1="900" y1="540" x2="1080" y2="620" />
          <line x1="110" y1="140" x2="180" y2="400" />
          <line x1="180" y1="400" x2="340" y2="420" />
        </g>

        {/* Constellation nodes */}
        {[
          [110, 140], [260, 210], [420, 120], [560, 280],
          [720, 180], [880, 320], [1040, 220], [1130, 120],
          [180, 400], [340, 420], [520, 540], [720, 620],
          [900, 540], [1080, 620],
        ].map(([x, y]) => (
          <g key={`node-${x}-${y}`} style={{ transformOrigin: `${x}px ${y}px` }}>
            <circle cx={x} cy={y} r="9" fill="url(#nodeGlow)">
              <animate attributeName="r" values="9;15;9" dur={`${2 + ((x + y) % 4) * 0.4}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.9;0.2;0.9" dur={`${2 + ((x + y) % 4) * 0.4}s`} repeatCount="indefinite" />
            </circle>
            <circle cx={x} cy={y} r="2.4" fill="#E85A18" opacity="0.9" />
          </g>
        ))}
      </svg>

      {/* ECG strip at bottom */}
      <div className="absolute bottom-4 left-0 right-0 h-16 opacity-70">
        <svg viewBox="0 0 1200 80" preserveAspectRatio="none" className="w-full h-full">
          <defs>
            <linearGradient id="ecg" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="transparent" />
              <stop offset="20%" stopColor="#FF7A2E" />
              <stop offset="80%" stopColor="#FF7A2E" />
              <stop offset="100%" stopColor="transparent" />
            </linearGradient>
            <linearGradient id="ecgFade" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#FF7A2E" stopOpacity="0" />
              <stop offset="50%" stopColor="#FF7A2E" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#FF7A2E" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M0 40 L200 40 L220 40 L240 20 L260 60 L280 10 L300 70 L320 40 L500 40 L520 40 L540 20 L560 60 L580 10 L600 70 L620 40 L800 40 L820 40 L840 20 L860 60 L880 10 L900 70 L920 40 L1200 40"
            stroke="url(#ecg)"
            strokeWidth="1.6"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="animate-[ecgScroll_6s_linear_infinite]"
            style={{ strokeDasharray: '1200', strokeDashoffset: 0 }}
          />
        </svg>
      </div>

      {/* Floating medical bubbles */}
      <FloatingBubble left="12%" top="22%" delay="0s" size={64}>DNA</FloatingBubble>
      <FloatingBubble left="78%" top="14%" delay="1.6s" size={54}>AI</FloatingBubble>
      <FloatingBubble left="70%" top="66%" delay="3s" size={72}>MRI</FloatingBubble>
      <FloatingBubble left="6%" top="70%" delay="2.2s" size={60}>ECG</FloatingBubble>
    </div>
  );
}

function FloatingBubble({ left, top, delay, size, children }) {
  return (
    <div
      className="absolute hidden md:flex items-center justify-center rounded-2xl bg-white/70 backdrop-blur-md border border-orange-100 shadow-[0_20px_60px_-30px_rgba(232,90,24,0.4)] animate-[floatY_6s_ease-in-out_infinite]"
      style={{ left, top, width: size, height: size, animationDelay: delay }}
    >
      <span
        className="text-[12px] font-semibold tracking-wider"
        style={{
          background: 'linear-gradient(135deg,#FF7A2E,#E85A18)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        {children}
      </span>
    </div>
  );
}
